<script setup lang="ts">
import type { SpecieResourceDetailed, SpeciePullResource } from "@carnet/api/backoffice";

const props = defineProps<{ detail: SpecieResourceDetailed | SpeciePullResource }>();

const { t } = useI18n();
const slots = useSlots();
const externalLink = computed(() => safeExternalHttpUrl(props.detail.externalLink));
</script>

<template>
  <div class="divide-y divide-blue-400">
    <h2 class="sr-only">{{ t("species.detail.title") }}</h2>

    <section v-if="detail.zones.length" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.zones") }}</h3>
      <ul class="columns-2 gap-4 -mb-2.5">
        <li v-for="(zone, index) in detail.zones" :key="index" class="mb-2.5">{{ zone.label }}</li>
      </ul>
    </section>

    <section v-if="detail.description" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.description") }}</h3>
      <p class="text whitespace-pre-line">{{ detail.description }}</p>
    </section>

    <section v-if="detail.specieLevel" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.level") }}</h3>
      <p>{{ detail.specieLevel.label }}</p>
    </section>

    <section v-if="detail.features.length" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.features") }}</h3>
      <ul class="flex flex-wrap gap-2">
        <li v-for="feature in detail.features" :key="feature.code">
          <UBadge color="neutral" variant="soft">{{ feature.label }}</UBadge>
        </li>
      </ul>
    </section>

    <section v-if="detail.protectionStatus" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.protectionStatus") }}</h3>
      <p>{{ detail.protectionStatus }}</p>
    </section>

    <section v-if="detail.inpnIdentifier" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.inpn") }}</h3>
      <p>{{ detail.inpnIdentifier }}</p>
    </section>

    <section v-if="detail.lookAlikeSpecies" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.similar") }}</h3>
      <p class="whitespace-pre-line">{{ detail.lookAlikeSpecies }}</p>
    </section>

    <section v-if="slots.observations" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.observations") }}</h3>
      <slot name="observations" />
    </section>

    <section v-if="externalLink" class="py-8">
      <h3 class="font-semibold uppercase mb-2.5">{{ t("species.detail.learn_more") }}</h3>
      <p v-if="detail.learnMore" class="mb-3">{{ detail.learnMore }}</p>
      <AppLogoDoris v-if="detail.learnMoreCode === 'doris'" class="h-7 w-auto mb-3 text-blue-700" />
      <UButton :href="externalLink" target="_blank" rel="noopener noreferrer" variant="link" trailing-icon="icon:external">
        {{ $t("species.detail.link") }}
      </UButton>
    </section>
  </div>
</template>
